import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase.js';
import Swal from 'sweetalert2';
import { 
    Copy, Check, AlertTriangle, UploadCloud, X,
    DollarSign, Zap, Network, Hash, Trash2
} from 'lucide-react';
import { toBase64 } from '../../utils/helpers.js';
import '../../styles/CrearDeposito.css';
import usePlatformConfig from '../../hooks/usePlatformConfig.js';

// --- INICIO DE LA MODIFICACIÓN: Componente WalletAddressRow mejorado con selección ---
const WalletAddressRow = ({ network, address, selected, onSelect }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => { navigator.clipboard.writeText(address); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    return (
        <div
            className={`wallet-card ${selected ? 'wallet-card-selected' : ''}`} 
            style={{ gap: '1rem', cursor: 'pointer' }}
            onClick={() => onSelect && onSelect(network)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onSelect && onSelect(network); }}
        > 
            <div className="wallet-details">
                <h4 className="wallet-network-title">{network}</h4>
                <p className="wallet-address-text truncate">{address}</p>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 8, justifyContent: 'center' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleCopy(); }} className="wallet-copy-btn">
                        {copied ? (<><Check size={16} /> Copiado</>) : (<><Copy size={16} /> Copiar</>) }
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onSelect && onSelect(network); }} className={`wallet-select-btn ${selected ? 'selected' : ''}`}>
                        {selected ? 'Seleccionada' : 'Seleccionar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- NUEVA FUNCIÓN DE OPTIMIZACIÓN ---
// Esta función toma un archivo de imagen, lo redimensiona y comprime.
const compressImage = (file, maxWidth = 1280, quality = 0.8) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const scaleRatio = maxWidth / img.width;
                const newWidth = img.width > maxWidth ? maxWidth : img.width;
                const newHeight = img.width > maxWidth ? img.height * scaleRatio : img.height;

                canvas.width = newWidth;
                canvas.height = newHeight;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);

                // Devuelve la imagen comprimida como un archivo Blob
                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                }, 'image/jpeg', quality);
            };
        };
        reader.onerror = error => reject(error);
    });
};

const CrearDeposito = ({ onAgreementCreated }) => {
    const [amount, setAmount] = useState('');
    const [depositNetwork, setDepositNetwork] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [acceleratorActive, setAcceleratorActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [proofImage, setProofImage] = useState(null);
    const [proofPreview, setProofPreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [selectedWallet, setSelectedWallet] = useState(null);

    const { config: platformConfig, loadingConfig } = usePlatformConfig();
    const availableWallets = useMemo(() => {
        if (!platformConfig || !Array.isArray(platformConfig.depositWallets)) {
            return [];
        }
        return platformConfig.depositWallets
            .map((wallet, index) => ({
                id: wallet?.id || `wallet-${index}`,
                label: (wallet?.label || '').trim(),
                address: (wallet?.address || '').trim(),
            }))
            .filter(wallet => wallet.label && wallet.address);
    }, [platformConfig]);

    const [amountError, setAmountError] = useState(''); // <-- NUEVO: Estado para el error del monto en tiempo real.
    const [txIdError, setTxIdError] = useState(''); // <-- NUEVO: Estado para el error del TxID en tiempo real.
    const [isDragging, setIsDragging] = useState(false); // <-- NUEVO: Estado para el drag-and-drop.
    const handleProofImageChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // --- MEJORA DE VALIDACIÓN: Tamaño del archivo ---
            if (file.size > 10 * 1024 * 1024) { // 10 MB
                setError('El archivo no debe superar los 10 MB.');
                setProofImage(null);
                setProofPreview('');
                return;
            }

            try {
                const compressedFile = await compressImage(file);
                setProofImage(compressedFile);
                setProofPreview(URL.createObjectURL(compressedFile));
                setError(''); // Limpiar error si la compresión es exitosa
            } catch (error) {
                console.error("Error al comprimir la imagen:", error);
                setError("No se pudo procesar la imagen seleccionada.");
            }
        }
    };

    // --- NUEVO: Handlers para el efecto de drag-and-drop ---
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        handleDragLeave(e); // Llama a leave para resetear el estado visual
        handleProofImageChange({ target: { files: e.dataTransfer.files } });
    };

    // --- NUEVO: useEffect para validación en tiempo real del monto ---
    useEffect(() => {
        const parsedAmount = parseFloat(amount);

        // No mostrar error si el campo está vacío
        if (amount === '' || isNaN(parsedAmount)) {
            setAmountError('');
            return;
        }

        if (parsedAmount < 100) {
            setAmountError('El monto mínimo de participación es de $100 USD.');
        } else if (parsedAmount % 50 !== 0) {
            setAmountError('El monto debe ser un múltiplo de $50 USD.');
        } else {
            setAmountError(''); // Limpiar error si el monto es válido
        }
    }, [amount]); // Este efecto se ejecuta cada vez que el 'amount' cambia.

    // --- NUEVO: useEffect para validación en tiempo real del TxID ---
    useEffect(() => {
        // No mostrar error si el campo está vacío
        if (transactionId.trim() === '') {
            setTxIdError('');
            return;
        }
        if (transactionId.trim().length < 10) {
            setTxIdError('El TxID parece demasiado corto. Por favor, verifícalo.');
        } else {
            setTxIdError('');
        }
    }, [transactionId]);

    const handleWalletSelection = (label) => {
        setDepositNetwork(label);
        const matchingWallet = availableWallets.find(wallet => wallet.label === label) || null;
        setSelectedWallet(matchingWallet);
        if (matchingWallet) {
            setCurrentStep(2);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const parsedAmount = parseFloat(amount);
        // --- CORRECCIÓN DE VALIDACIÓN: Mínimo y múltiplo ---
        // 1. Validar que sea un número y que sea al menos 100.
        if (isNaN(parsedAmount) || parsedAmount < 100) { 
            Swal.fire('Monto Inválido', 'El monto mínimo de participación es de $100 USD.', 'error');
            return; 
        }
        // 2. Validar que sea un múltiplo de 50.
        if (parsedAmount % 50 !== 0) { setError('El monto debe ser un múltiplo de $50 USD.'); return; }
        if (!depositNetwork) { setError('Por favor, selecciona la red que usaste para el aporte.'); return; }
        // --- MEJORA DE VALIDACIÓN: Formato básico de TxID ---
        if (!transactionId.trim() || transactionId.trim().length < 10) { 
            Swal.fire('Dato Faltante', 'El ID de la transacción (TxID) es obligatorio y debe ser válido.', 'error');
            return; 
        }
    // Enforce that a proof image is attached (all steps mandatory)
    if (!proofImage) { Swal.fire('Dato Faltante', 'Por favor, adjunta una imagen del comprobante de pago. Es obligatoria.', 'error'); return; }
        
        setLoading(true);

        try {
            const user = auth.currentUser;
            if (!user) throw new Error("Usuario no autenticado.");

            let downloadURL = null;
            if (proofImage) {
                setUploading(true);
                const functions = getFunctions();
                const uploadFile = httpsCallable(functions, 'uploadFileCallable');
                const base64File = await toBase64(proofImage);
                const filePath = `proofs/${user.uid}/${Date.now()}_${proofImage.name}`;
                let result = null;
                try {
                    // Primero intentamos el callable (httpsCallable)
                    result = await uploadFile({ file: base64File, path: filePath });
                    console.log('uploadFile callable result:', result);
                    // Algunos handlers devuelven { data: { downloadURL } }
                    // pero httpsCallable a veces envuelve otro `data` -> { data: { data: { downloadURL } } }
                    downloadURL = (result && result.data && (result.data.downloadURL || (result.data.data && result.data.data.downloadURL))) || null;
                } catch (uploadErr) {
                    console.warn('Upload callable failed, attempting onRequest fallback:', uploadErr);
                    // Intento fallback: POST directo al endpoint onRequest
                    try {
                        const uploadUrl = 'https://us-central1-plataforma-invest-f1675.cloudfunctions.net/uploadFile';
                        const resp = await fetch(uploadUrl, {
                            method: 'POST',
                            // El endpoint exige sesión (ver functions/index.js, uploadFile).
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` },
                            body: JSON.stringify({ data: { file: base64File, path: filePath } })
                        });
                        if (!resp.ok) {
                            const errBody = await resp.text();
                            console.error('Fallback upload failed response:', resp.status, errBody);
                            throw new Error('Fallback upload failed');
                        }
                        const respJson = await resp.json();
                        console.log('Fallback upload result:', respJson);
                        downloadURL = respJson && respJson.data && respJson.data.downloadURL ? respJson.data.downloadURL : null;
                    } catch (fallbackErr) {
                        console.error('Fallback upload error:', fallbackErr);
                        setUploading(false);
                        setLoading(false);
                        setError('Error al subir la imagen (callable y fallback fallaron). Intenta de nuevo.');
                        return;
                    }
                }
                setUploading(false);

                // If upload did not return a URL, abort to avoid creating an agreement without proof
                if (!downloadURL) {
                    console.error('Upload did not return a downloadURL:', result);
                    setLoading(false);
                    setError('No se pudo obtener la URL pública del comprobante. Intenta subir la imagen de nuevo.');
                    return;
                }
            }

            const privateAgreementRef = doc(collection(db, `users/${user.uid}/agreements`));
            const publicAgreementRef = doc(db, 'public_agreements', privateAgreementRef.id);

            // --- VALIDACIÓN DE CAMPOS CLAVE ---
            if (!privateAgreementRef.path) throw new Error('Error: privateAgreementPath no se generó correctamente.');
            if (!user.email) throw new Error('Error: El usuario no tiene email.');
            if (!parsedAmount || parsedAmount < 100) throw new Error('Error: Monto inválido.');
            // --- FIN VALIDACIÓN ---

            const walletForSubmission = selectedWallet || availableWallets.find(wallet => wallet.label === depositNetwork) || null;

            const agreementData = {
                userId: user.uid,
                userEmail: user.email,
                amount: parsedAmount,
                depositNetwork,
                depositWalletAddress: walletForSubmission?.address || null,
                transactionId: transactionId.trim(),
                acceleratorActive,
                // Guardar both fields for compatibility: admin views check `proofUrl`, some legacy flows use `paymentProofUrl`.
                proofUrl: downloadURL || null,
                paymentProofUrl: downloadURL || null,
                status: 'pendiente',
                createdAt: serverTimestamp(),
                currentValue: 0,
                dailyAccumulation: 0,
                privateAgreementPath: privateAgreementRef.path
            };
            // Log para depuración
            console.log('Creando acuerdo con datos:', agreementData);

            const batch = writeBatch(db);
            batch.set(privateAgreementRef, agreementData);
            batch.set(publicAgreementRef, agreementData);
            await batch.commit();
            
            Swal.fire({
                title: '¡Solicitud Enviada!',
                text: 'Tu solicitud ha sido registrada y será revisada pronto.',
                icon: 'success',
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
                willClose: () => {
                    onAgreementCreated();
                }
            });
            
        } catch (err) {
            const errorMessage = err.message || 'Ocurrió un error al procesar tu solicitud.';
            setError(errorMessage);
            Swal.fire('Error', errorMessage, 'error');
            console.error("Error detallado:", err);
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    // Interactive helpers: quick selection and validation for steps
    const isStep2Valid = () => {
        return !!depositNetwork && !!transactionId.trim() && transactionId.trim().length >= 10;
    };

    const [currentStep, setCurrentStep] = useState(1);
    const activeStep = currentStep;

    return (
        <div className="main-panel">
            <h2 className="section-title">Iniciar Nuevo Acuerdo Comercial</h2>
            <div className="deposit-layout">
                <div className="left-column">
                    <div className="stepper">
                        <div className={`step ${activeStep === 1 ? 'active' : activeStep > 1 ? 'completed' : ''}`}>
                            <div className="circle">1</div>
                            <div className="label">Aporte</div>
                        </div>
                        <div className={`step ${activeStep === 2 ? 'active' : activeStep > 2 ? 'completed' : ''}`}>
                            <div className="circle">2</div>
                            <div className="label">Detalles</div>
                        </div>
                        <div className={`step ${activeStep === 3 ? 'active' : ''}`}>
                            <div className="circle">3</div>
                            <div className="label">Comprobante</div>
                        </div>
                    </div>
                    {currentStep === 1 && (
                    <div className="form-step">
                        <h3>1. Realiza tu aporte a una de nuestras billeteras:</h3>
                        <div className="wallets-container">
                            {loadingConfig ? (
                                <div className="message-box info">Cargando billeteras disponibles...</div>
                            ) : (
                                availableWallets.length > 0 ? (
                                    availableWallets.map(wallet => (
                                        <WalletAddressRow
                                            key={wallet.id}
                                            network={wallet.label}
                                            address={wallet.address}
                                            selected={depositNetwork === wallet.label}
                                            onSelect={handleWalletSelection}
                                        />
                                    ))
                                ) : (
                                    <div className="message-box warning">
                                        Aún no se han configurado billeteras de aporte. Contacta a soporte antes de continuar.
                                    </div>
                                )
                            )}
                        </div>
                        <div className="message-box danger"><strong>¡Atención!</strong> Asegúrate de enviar los fondos por la red correcta. Guarda el ID de la transacción (TxID) para el siguiente paso.</div>
                    </div>
                    )}

                    {/* Step 2: detalles (se muestra solo si currentStep === 2) */}
                    {currentStep === 2 && (
                        <div className="form-step">
                            <h3>2. Completa los detalles de tu participación:</h3>
                            {/* --- MEJORA DE UX: Resumen dinámico --- */}
                            {parseFloat(amount) >= 100 && (
                                <div className="investment-summary-box">
                                    <p>Monto a participar: <strong>${parseFloat(amount).toFixed(2)}</strong></p>
                                    {acceleratorActive && <p>Comisión Acelerador (5%): <strong>-${(parseFloat(amount) * 0.05).toFixed(2)}</strong></p>}
                                    <p>Monto activo neto estimado: <strong>${(acceleratorActive ? parseFloat(amount) * 0.95 : parseFloat(amount)).toFixed(2)}</strong></p>
                                </div>
                            )}
                            <form className="deposit-form">
                                {error && <div className="message-box error">{error}</div>}
                                <div className="form-grid">                                    
                                    <div className="input-group">
                                        <label>Monto del Acuerdo ($ USD)</label>
                                        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Ej: 1000" step="50" min="100" required />
                                        {/* --- NUEVO: Mostrar el mensaje de error en tiempo real --- */}
                                        {amountError ? (
                                            <p className="input-error-text">{amountError}</p>
                                        ) : amount !== '' && (
                                            <p className="input-help-text">Monto mínimo $100 y múltiplo de $50.</p>
                                        )}
                                    </div>
                                    <div className="input-group">
                                        <label>Red de Aporte</label>
                                        <select
                                            value={depositNetwork}
                                            onChange={e => handleWalletSelection(e.target.value)}
                                            required
                                            disabled={availableWallets.length === 0}
                                        >
                                            <option value="" disabled>Selecciona la red que usaste</option>
                                            {availableWallets.map(wallet => (
                                                <option key={wallet.id} value={wallet.label}>{wallet.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-grid-compact">
                                    <div className="input-group">
                                        <label>ID de Transacción (TxID)</label>
                                        <input type="text" value={transactionId} onChange={e => setTransactionId(e.target.value)} placeholder="Pega aquí el hash o ID" required />
                                        {txIdError && (
                                            <p className="input-error-text">{txIdError}</p>
                                        )}
                                    </div>
                                    <div className="accelerator-column">
                                        <div className="checkbox-group centered">
                                            <input type="checkbox" id="accelerator" checked={acceleratorActive} onChange={e => setAcceleratorActive(e.target.checked)} />
                                            <label htmlFor="accelerator" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
                                                <strong>Activar Acelerador</strong>
                                                <span className="input-help-text">Ganancias de 0.9% a 1.2% diario</span>
                                            </label>
                                        </div>
                                        {/* --- NUEVO: Aviso sobre comisiones y acelerador --- */}
                                        <div className="commission-warning-box">
                                            <AlertTriangle size={36} />
                                            <div>
                                                <strong>¡Importante!</strong> Para recibir el pago de tus <strong>comisiones por referidos</strong>, debes tener al menos un acuerdo con el Acelerador activo.
                                            </div>
                                        </div>
                                        {acceleratorActive && (<div className="accelerator-info-box">Se aplicará un <strong>fee único del 5%</strong> sobre el capital al ser aprobado.</div>)}
                                    </div>
                                </div>
                                <div className="form-step-buttons">
                                    <button type="button" className="btn-secondary" onClick={() => setCurrentStep(1)}>Anterior</button>
                                    <button type="button" className="login-button" onClick={() => setCurrentStep(3)} disabled={!!amountError || !!txIdError || !isStep2Valid()}>Siguiente</button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Step 3: comprobante (se muestra solo si currentStep === 3) */}
                    {currentStep === 3 && (
                        <div className="form-step">
                            <h3>3. Adjunta el comprobante de pago</h3>
                            <form onSubmit={handleSubmit} className="deposit-form">
                                {error && <div className="message-box error">{error}</div>}
                                <div className="input-group">
                                    <label>Adjuntar Comprobante de Pago</label>
                                    {/* --- MEJORA VISUAL: Contenedor de carga de archivos --- */}
                                    <div 
                                        className={`file-uploader ${isDragging ? 'drag-over' : ''}`}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragEnter} // Necesario para que onDrop funcione
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input type="file" id="proof-upload" accept="image/png, image/jpeg" onChange={handleProofImageChange} style={{display: 'none'}} />
                                        
                                        {proofPreview ? (
                                            <div className="image-preview-container filled">
                                                <img src={proofPreview} alt="Vista previa" className="image-preview" />
                                                <button type="button" className="remove-image-btn" onClick={() => { setProofImage(null); setProofPreview(''); }}><X size={16} /></button>
                                            </div>
                                        ) : (
                                            <label htmlFor="proof-upload" className="file-upload-label"><UploadCloud size={32} /><span>{proofImage ? proofImage.name : 'Haz clic o arrastra una imagen aquí'}</span><small>PNG o JPG, máx 10MB</small></label>
                                        )}
                                    </div>
                                </div>
                                <div className="form-step-buttons">
                                    <button type="button" className="btn-secondary" onClick={() => setCurrentStep(2)}>Anterior</button>
                                    <button type="submit" disabled={loading || uploading || !proofImage} className="login-button">{uploading ? 'Subiendo imagen...' : loading ? 'Registrando...' : 'Registrar Acuerdo Comercial'}</button>
                                </div>
                            </form>
                        </div>
                    )}
            <div className="message-box warning"><AlertTriangle size={24} /><div><strong>¡Aviso Importante!</strong><p>Una vez que tu acuerdo sea aprobado, comenzarás a percibir la participación asignada después de las <strong>72 horas hábiles</strong> siguientes.</p></div></div>
                </div>

                <aside className="right-column">
                    <div className="summary-card">
                        <h4 className="summary-title">Resumen del Acuerdo</h4>

                        <div className="summary-section">
                            <div className="summary-item">
                                <span className="summary-label"><DollarSign size={16} className="summary-icon" /> Monto</span>
                                <strong className="summary-value">${(parseFloat(amount) || 0).toFixed(2)}</strong>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label"><Zap size={16} className="summary-icon" /> Acelerador</span>
                                <strong className="summary-value">{acceleratorActive ? 'Activado' : 'No'}</strong>
                            </div>
                            {acceleratorActive && (
                                <div className="summary-item fee-item">
                                    <span className="summary-label">Fee (5%)</span>
                                    <strong className="summary-value fee-amount">-${((parseFloat(amount) || 0) * 0.05).toFixed(2)}</strong>
                                </div>
                            )}
                        </div>

                        <div className="highlight-box">
                            <span className="summary-label">Monto neto estimado</span>
                            {/* <strong className="highlight-value">${(isNaN(netAfterFee) ? 0 : netAfterFee).toFixed(2)}</strong> */}
                        </div>

                        <div className="summary-section">
                            <div className="summary-item">
                                <span className="summary-label"><Network size={16} className="summary-icon" /> Red</span>
                                <strong className="summary-value">{depositNetwork || 'No seleccionada'}</strong>
                            </div>
                            {selectedWallet && (
                                <div className="summary-item">
                                    <span className="summary-label"><Network size={16} className="summary-icon" /> Dirección</span>
                                    <strong className="summary-value mono">{selectedWallet.address}</strong>
                                </div>
                            )}
                            <div className="summary-item">
                                <span className="summary-label"><Hash size={16} className="summary-icon" /> TxID</span>
                                <strong className="summary-value mono">{transactionId ? (transactionId.length > 15 ? `${transactionId.substring(0,6)}...${transactionId.substring(transactionId.length-4)}` : transactionId) : '—'}</strong>
                            </div>
                        </div>

                        {proofPreview && (<div className="proof-preview"><img src={proofPreview} alt="Comprobante" /></div>)}
                        
                        <div className="summary-actions">
                            <button
                                type="button"
                                className="btn-secondary clear-btn"
                                onClick={() => {
                                    setAmount('');
                                    setDepositNetwork('');
                                    setSelectedWallet(null);
                                    setTransactionId('');
                                    setProofImage(null);
                                    setProofPreview('');
                                    setAcceleratorActive(false);
                                }}
                            >
                                <Trash2 size={14} /> Limpiar Formulario
                            </button>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default CrearDeposito;